import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

@Component({
    selector: 'fcp-pagination',
    templateUrl: './fcp-pagination.component.html',
    styleUrls: ['./fcp-pagination.component.less'],
    standalone: false
})
export class FcpPaginationComponent implements OnChanges {
    @Input() public previousPageTriggerLabel = 'Previous';
    @Input() public nextPageTriggerLabel = 'Next';
    @Input() public totalPageItems: number;
    @Input() public page = 1;
    @Input() public pageSize = 50;
    @Output() public pageChange: EventEmitter<number> = new EventEmitter<number>();
    private readonly edgePadding = 1;
    private readonly selectedPagePadding = 1;

    ngOnChanges(changes: SimpleChanges) {
        if (
            (changes.totalPageItems || changes.pageSize) &&
            (changes.page?.currentValue || this.page) > this.pageCount
        ) {
            this.pageChange.emit(-1);
        }
    }

    // Basic list of items to render
    // e.g. pageTriggerList = [1, 'ellipsis', 5, 'ellipsis', 10]
    public get pageTriggerList() {
        return [
            ...this.startPages,

            // Start ellipsis
            ...(this.siblingsStart > this.edgePadding + 2
                ? ['ellipsis']
                : this.edgePadding + 1 < this.pageCount - this.edgePadding
                  ? [this.edgePadding + 1]
                  : []),

            // Sibling pages
            ...FcpPaginationComponent.range(this.siblingsStart, this.siblingsEnd),

            // End ellipsis
            ...(this.siblingsEnd < this.pageCount - this.edgePadding - 1
                ? ['ellipsis']
                : this.pageCount - this.edgePadding > this.edgePadding
                  ? [this.pageCount - this.edgePadding]
                  : []),

            ...this.endPages
        ];
    }

    public get pageCount() {
        return Math.ceil(this.totalPageItems / this.pageSize);
    }

    private get startPages() {
        return FcpPaginationComponent.range(1, Math.min(this.edgePadding, this.pageCount));
    }

    private get endPages() {
        return FcpPaginationComponent.range(
            Math.max(this.pageCount - this.edgePadding + 1, this.edgePadding + 1),
            this.pageCount
        );
    }

    private get siblingsStart() {
        return Math.max(
            Math.min(
                // Natural start
                this.page - this.selectedPagePadding,
                // Lower boundary when page is high
                this.pageCount - this.edgePadding - this.selectedPagePadding * 2 - 1
            ),
            // Greater than startPages
            this.edgePadding + 2
        );
    }

    private get siblingsEnd() {
        return Math.min(
            Math.max(
                // Natural end
                this.page + this.selectedPagePadding,
                // Upper boundary when page is low
                this.edgePadding + this.selectedPagePadding * 2 + 2
            ),
            // Less than endPages
            this.endPages.length > 0 ? this.endPages[0] - 2 : this.pageCount - 1
        );
    }

    public onClickPageTrigger(page: number) {
        if (page === 0) {
            return;
        }
        if (page === this.pageCount + 1) {
            return;
        }

        this.pageChange.emit(page);
    }

    // https://dev.to/namirsab/comment/2050
    private static range(start: number, end: number): number[] {
        const length = end - start + 1;
        return Array.from({ length }, (_, i) => start + i);
    }
}
